import logo from '@assets/odbpromo.png'
import { ReactNode, useRef } from 'react'
import {
    Badge,
    BadgeFooter,
    CardBack,
    CardFront,
    CardStack,
    ClipStrap,
    Container,
    Cord,
    Crimp,
    Heading,
    Lanyard,
    LoginCard,
    Logo,
    LogoContainer,
    Reel,
    Ring,
    Strap,
    Turner,
} from './Login.styles'

const STRAP_TEXT = Array(24).fill('ODB PROMO').join(' • ')

interface FaceProps {
    title: string
    subtitle?: string
    footer?: ReactNode
    compact?: boolean
    children: ReactNode
}

/** Logo, heading, the face's content and the colored band at the bottom. */
export const BadgeFace = ({ title, subtitle, footer = 'ODHUB', compact, children }: FaceProps) => (
    <>
        <LogoContainer $compact={compact}>
            <Logo src={logo} alt="ODB Promo" $compact={compact} />
            <Heading>
                <h1>{title}</h1>
                {subtitle && <p>{subtitle}</p>}
            </Heading>
        </LogoContainer>
        {children}
        <BadgeFooter>{footer}</BadgeFooter>
    </>
)

interface Props {
    /** Front face content. */
    children: ReactNode
    /** Back face content; without it the card does not flip. */
    back?: ReactNode
    flipped?: boolean
}

/** The sign-in card, drawn as a badge hanging from a lanyard around the neck. */
export const LoginBadge = ({ children, back, flipped = false }: Props) => {
    const isFlipped = Boolean(back) && flipped
    // Animate only real turns, never the state the page opened with.
    const initialFlipped = useRef(isFlipped)
    const turned = useRef(false)
    if (isFlipped !== initialFlipped.current) turned.current = true

    return (
        <Container>
            <Badge $flipped={isFlipped} $turned={turned.current}>
                <Lanyard aria-hidden="true">
                    <Strap $side="left">
                        <span>{STRAP_TEXT}</span>
                    </Strap>
                    <Strap $side="right">
                        <span>{STRAP_TEXT}</span>
                    </Strap>
                    <Crimp />
                </Lanyard>
                <Ring aria-hidden="true" />
                <Reel aria-hidden="true">ODB</Reel>
                <Turner $flipped={isFlipped} $turned={turned.current}>
                    <Cord aria-hidden="true" />
                    <ClipStrap aria-hidden="true" />
                    <CardStack>
                        {back ? (
                            <>
                                <CardFront $active={!flipped} aria-hidden={flipped}>
                                    {children}
                                </CardFront>
                                <CardBack $active={flipped} aria-hidden={!flipped}>
                                    {back}
                                </CardBack>
                            </>
                        ) : (
                            <LoginCard $active>{children}</LoginCard>
                        )}
                    </CardStack>
                </Turner>
            </Badge>
        </Container>
    )
}
